import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild,
  OnDestroy,
  afterNextRender,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface DropdownOption<T = string | number | boolean | null> {
  value: T;
  label: string;
}

@Component({
  selector: 'app-dropdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
})
export class DropdownComponent<T = string | number | boolean | null> implements OnDestroy {
  /** Etiqueta opcional encima o al lado del trigger */
  label = input<string>('');
  /** Opciones: { value, label } */
  options = input.required<DropdownOption<T>[]>();
  /** Valor actualmente seleccionado */
  value = input<T | null | undefined>(null);
  /** Texto cuando no hay selección */
  placeholder = input<string>('Seleccionar');
  /** Deshabilitado */
  disabled = input<boolean>(false);
  /** Ancho mínimo del trigger (ej. '120px', '10rem') */
  minWidth = input<string>('');
  /** Ancho máximo del trigger (ej. '120px', '10rem') */
  maxWidth = input<string>('');
  /** Posición del panel respecto al trigger: 'top' | 'bottom' | 'left' | 'right'. */
  position = input<'top' | 'bottom' | 'left' | 'right'>('bottom');
  /** Renderiza el panel con position:fixed para escapar contenedores con overflow. */
  overlay = input<boolean>(false);
  /** Con overlay, invierte arriba/abajo si no hay espacio suficiente. */
  autoFlip = input<boolean>(true);

  /** Si está activo, volver a hacer click en la opción seleccionada emite `deselectValue`. */
  allowDeselectOnReselect = input<boolean>(false);
  /** Valor a emitir cuando se deselecciona por re-selección (debe ser del mismo tipo T). */
  deselectValue = input<T | undefined>(undefined);

  valueChange = output<T>();

  triggerRef = viewChild<ElementRef<HTMLButtonElement>>('trigger');
  panelRef = viewChild<ElementRef<HTMLDivElement>>('panel');

  isOpen = signal(false);
  overlayPanelStyle = signal<Record<string, string>>({});

  className = input<string>('gap-2 items-center flex');

  private cdr = inject(ChangeDetectorRef);
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private panelOriginalParent: HTMLElement | null = null;
  private panelOriginalNextSibling: Node | null = null;
  private readonly onScrollCapture = (): void => {
    if (this.isOpen() && this.overlay()) {
      this.updateOverlayPosition();
    }
  };

  constructor() {
    afterNextRender(() => {
      document.addEventListener('scroll', this.onScrollCapture, true);
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onScrollCapture, true);
    this.restorePanelFromBody();
  }

  private closePanel(): void {
    this.clearOverlayPanelInlineSize();
    this.restorePanelFromBody();
    this.isOpen.set(false);
    this.overlayPanelStyle.set({});
  }

  private attachPanelToBody(): void {
    const panel = this.panelRef()?.nativeElement;
    if (!panel || panel.parentElement === document.body) return;

    this.panelOriginalParent = panel.parentElement;
    this.panelOriginalNextSibling = panel.nextSibling;

    const hostClass = this.hostEl.nativeElement.className;
    if (hostClass) {
      hostClass
        .split(/\s+/)
        .filter(Boolean)
        .forEach((cls: string) => panel.classList.add(cls));
    }

    document.body.appendChild(panel);
  }

  private restorePanelFromBody(): void {
    const panel = this.panelRef()?.nativeElement;
    if (!panel || !this.panelOriginalParent) return;

    if (this.panelOriginalNextSibling) {
      this.panelOriginalParent.insertBefore(panel, this.panelOriginalNextSibling);
    } else {
      this.panelOriginalParent.appendChild(panel);
    }

    this.panelOriginalParent = null;
    this.panelOriginalNextSibling = null;
  }

  /** Label del valor actual para el botón (derivado de value + options). */
  getDisplayLabel(): string {
    return this.computeLabelFromValue();
  }

  private computeLabelFromValue(): string {
    const v = this.value();
    const opts = this.options();
    if (v === null || v === undefined) {
      const empty = opts.find((o) => o.value === null || o.value === undefined || o.value === '');
      return empty?.label ?? this.placeholder();
    }
    const found = opts.find((o) => o.value === v);
    return found?.label ?? this.placeholder();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const target = event.target as Node;
    const triggerEl = this.triggerRef()?.nativeElement;
    const panelEl = this.panelRef()?.nativeElement;
    if (
      triggerEl?.contains(target) ||
      panelEl?.contains(target)
    ) return;
    this.closePanel();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChange(): void {
    if (this.isOpen() && this.overlay()) {
      this.updateOverlayPosition();
    }
  }

  toggle(): void {
    if (this.disabled()) return;
    const next = !this.isOpen();
    if (!next) {
      this.closePanel();
      return;
    }

    this.isOpen.set(true);
    if (this.overlay()) {
      this.cdr.detectChanges();
      this.attachPanelToBody();
      this.updateOverlayPosition();
      requestAnimationFrame(() => {
        this.updateOverlayPosition();
        requestAnimationFrame(() => this.updateOverlayPosition());
      });
    }
  }

  private updateOverlayPosition(): void {
    const trigger = this.triggerRef()?.nativeElement;
    const panel = this.panelRef()?.nativeElement;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const panelHeight = panel?.offsetHeight ?? 256;
    const maxPanelHeight = Math.min(256, panelHeight);

    let placement: 'top' | 'bottom' =
      this.position() === 'top' ? 'top' : 'bottom';

    if (this.autoFlip()) {
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      if (
        placement === 'bottom' &&
        spaceBelow < maxPanelHeight &&
        spaceAbove > spaceBelow
      ) {
        placement = 'top';
      } else if (
        placement === 'top' &&
        spaceAbove < maxPanelHeight &&
        spaceBelow > spaceAbove
      ) {
        placement = 'bottom';
      }
    }

    if (placement === 'top') {
      const styles = {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        minWidth: `${rect.width}px`,
        maxWidth: `${rect.width}px`,
        bottom: `${window.innerHeight - rect.top + gap}px`,
        maxHeight: `${Math.max(120, Math.min(256, rect.top - gap - 8))}px`,
        zIndex: '1200',
        boxSizing: 'border-box',
      };
      this.overlayPanelStyle.set(styles);
      this.applyOverlayPanelSize(panel, rect.width, styles);
    } else {
      const styles = {
        position: 'fixed',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        minWidth: `${rect.width}px`,
        maxWidth: `${rect.width}px`,
        top: `${rect.bottom + gap}px`,
        maxHeight: `${Math.max(120, Math.min(256, window.innerHeight - rect.bottom - gap - 8))}px`,
        zIndex: '1200',
        boxSizing: 'border-box',
      };
      this.overlayPanelStyle.set(styles);
      this.applyOverlayPanelSize(panel, rect.width, styles);
    }
    this.cdr.markForCheck();
  }

  private applyOverlayPanelSize(
    panel: HTMLDivElement | undefined,
    width: number,
    styles: Record<string, string>,
  ): void {
    if (!panel) return;

    panel.style.setProperty('width', `${width}px`, 'important');
    panel.style.setProperty('min-width', `${width}px`, 'important');
    panel.style.setProperty('max-width', `${width}px`, 'important');
    panel.style.setProperty('box-sizing', 'border-box', 'important');

    Object.entries(styles).forEach(([key, value]) => {
      panel.style.setProperty(this.toCssProperty(key), value);
    });
  }

  private toCssProperty(key: string): string {
    return key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
  }

  private clearOverlayPanelInlineSize(): void {
    const panel = this.panelRef()?.nativeElement;
    if (!panel) return;

    panel.style.removeProperty('width');
    panel.style.removeProperty('min-width');
    panel.style.removeProperty('max-width');
  }

  select(option: DropdownOption<T>): void {
    const currentValue = this.value();
    const shouldDeselect = this.allowDeselectOnReselect() && currentValue === option.value;
    const emitVal = shouldDeselect ? (this.deselectValue() as T) : option.value;
    this.valueChange.emit(emitVal);
    this.closePanel();
    this.cdr.detectChanges();
  }

  isSelected(option: DropdownOption<T>): boolean {
    const v = this.value();
    const optVal = option.value;
    if (v === optVal) return true;
    if (v === null || v === undefined) return optVal === null || optVal === undefined || optVal === '';
    return v === optVal;
  }
}
